import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { fileNameHelper, FileUploadCustomFormField, FileUploadCustomFormFieldChunkSize, FileUploadCustomFormFieldData, FileUploadCustomFormFieldMaxFileSize, rng } from '../../../../amorg-common';
import { deleteCustomFormAnswerFieldVideo, submitCustomFormAnswerFieldVideoChunk } from '../../../../api/customFormClientApi';
import { Card } from '../../../../designSystem/Card';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { Modal } from '../../../../designSystem/Modal';
import { Button } from '../../../../designSystem/button/Button';
import { ButtonRow } from '../../../../designSystem/button/ButtonRow';
import { colors } from '../../../../designSystem/colors/color';
import { DeleteConfirmationButton } from '../../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { FileUploadButton } from '../../../../designSystem/formField/FileUploadButton';
import { HorizontalFillBar } from '../../../../designSystem/graphs/HorizontalFillBar';
import { P } from '../../../../designSystem/typography/Body';
import { CustomFormFieldHeader } from '../../[parm]/_components/CustomFormFieldHeader';
import { AnswerFieldContext } from '../customFormFieldFactory';

type FileUploadCustomFormFieldComponentProps = AnswerFieldContext & {
    field: FileUploadCustomFormField;
};

type State =
    {
        stage: 'pending';
    } |
    {
        stage: 'uploading',
        uploadSessionId: string,
        chunk: number,
        totalChunks: number;
    } |
    {
        stage: 'error',
        error: 'file_too_big' | 'invalid_file_name' | 'invalid_file_type' | 'upload_failed';
    };

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function FileUploadCustomFormFieldComponent (props: FileUploadCustomFormFieldComponentProps): JSX.Element
{
    const [state, setState] = React.useState<State>({ stage: 'pending' });
    const [loading, setLoading] = React.useState(false);
    const [fileInputKey, setFileInputKey] = React.useState(rng.getRandomId());

    const abortController = React.useRef<AbortController | null>(null);


    async function handleClickUpload (file: File): Promise<void>
    {
        if (file.size > FileUploadCustomFormFieldMaxFileSize)
            setState({ stage: 'error', error: 'file_too_big' });

        else if (!fileNameHelper.isValidPublicFileName(file.name))
            setState({ stage: 'error', error: 'invalid_file_name' });

        else if (!fileNameHelper.getSupportedFileExtension(file.name))
            setState({ stage: 'error', error: 'invalid_file_type' });

        else
        {
            abortController.current = new AbortController();

            try
            {
                await sendChunkedFile(
                    file,
                    abortController.current.signal
                );
                setFileInputKey(rng.getRandomId());
            }
            catch (e)
            {
                if (e === 'cancelled')
                    setState({ stage: 'pending' });
                else
                {
                    setState({ stage: 'error', error: 'upload_failed' });
                    console.log(e);
                }
            }
            finally
            {
                abortController.current = null;
            }
        }
    }

    async function handleClickCancel (): Promise<void>
    {
        if (abortController)
        {
            abortController.current.abort('cancelled');
        }
    }

    async function handleClickDelete (): Promise<void>
    {
        setLoading(true);
        await deleteCustomFormAnswerFieldVideo(props.siteId, props.answer.id, props.field.fieldId);
        await props.doUpdate();
        setLoading(false);
    }

    async function sendChunkedFile (file: File, abortSignal: AbortSignal): Promise<void>
    {
        const uploadSessionId = rng.getRandomId();
        const fileSize = file.size;
        const chunkSize = FileUploadCustomFormFieldChunkSize;
        const totalChunks = Math.ceil(file.size / chunkSize);
        setState({ stage: 'uploading', uploadSessionId, totalChunks, chunk: 0 });

        for (let i = 0; i < totalChunks; i++)
        {
            const start = i * chunkSize;
            const end = Math.min(file.size, (i + 1) * chunkSize);
            const chunk = file.slice(start, end);
            const success = await sendChunk(chunk, i, totalChunks, uploadSessionId, fileSize, file.name, abortSignal);
            if (!success)
            {
                setState({ stage: 'error', error: 'upload_failed' });
                return;
            }
            setState({ stage: 'uploading', uploadSessionId, totalChunks, chunk: i + 1 });
        }
        setState({ stage: 'pending' });

        props.doUpdate();
    }

    async function sendChunk (chunk: Blob, chunkIndex: number, totalChunks: number, uploadSessionId: string, fileSize: number, fileName: string, abortSignal: AbortSignal): Promise<boolean>
    {
        const result = await submitCustomFormAnswerFieldVideoChunk(
            props.siteId,
            props.answer.id,
            props.field.fieldId,
            {
                uploadSessionId,
                fileName,
                fileSize,
                chunkIndex,
                totalChunks
            },
            chunk,
            abortSignal
        );
        return result.isSuccess;
    }

    const field = props.answer.selections.find(f => f.fieldId === props.field.fieldId);

    let data: FileUploadCustomFormFieldData = null;
    if (field?.value)
        data = JSON.parse(field.value) as FileUploadCustomFormFieldData;

    console.log(data);

    return (
        <section>
            <LoadingPageOverlay loading={loading} />
            
            <CustomFormFieldHeader field={props.field} />
    
            <Card variant="outlined">
                <Flex flexDirection="row" alignItems='center' gap={24}>

                    <VideoComponent data={data} onClickDelete={handleClickDelete} />

                    <Flex flexDirection="column" gap={8}>
                        <FileUploadButton
                            // When key changes, input is refreshed allowing to upload the same file again
                            key={fileInputKey}
                            disabled={props.disabled}
                            inputName={'upload_file'}
                            size='42px'
                            text={data && data.status.stage==='complete' ? 'Upload ny video' : 'Upload video'}
                            fileTypes={['mp4', 'mov']}
                            onClickUpload={handleClickUpload}
                        />
                        {data && data.status.stage === 'complete' &&
                            <DeleteConfirmationButton
                                disabled={props.disabled}
                                buttonText='Slet video'
                                confirmationHeading='Slet video'
                                confirmationText='Er du sikker på at du vil slette videoen ? Denne handling kan ikke fortrydes.'
                                onConfirmDelete={handleClickDelete}
                                requireKey={false}
                                variant='destructive'
                            />
                        }
                    </Flex>


                </Flex>
            </Card>

            <Modal title='Uploader video'
                showModal={state.stage !== 'pending'}
                onCancel={handleClickCancel}
                content={
                    <>
                        {state.stage === 'error' &&
                            <>
                                <P>Der opstod en fejl. Prøv igen senere.</P>
                                <Button variant='filled' text='Luk' onClick={() => setState({ stage: 'pending' })} />
                            </>
                        }
                        {state.stage === 'uploading' &&
                            <>
                                <HorizontalFillBar
                                    fillColor={colors.graphs.progressBar}
                                    fillPct={(state.chunk / (state.totalChunks - 1)) * 100}
                                    border="1px solid #4E4BFB3d"
                                />
                                <ButtonRow marginTop={10} marginBottom={10}>
                                    <Button variant='outlined' text='Annuller' onClick={handleClickCancel} />
                                </ButtonRow>
                            </>
                        }

                    </>
                } />
        </section>
    );
}


type VideoComponentProps = {
    data: FileUploadCustomFormFieldData | null,
    onClickDelete:()=>void
};
type ThumbLoadStatus = { status:'waiting' | 'loaded' } | { status:'error', retries:number };
function VideoComponent (props: VideoComponentProps): JSX.Element
{
    const [imageHash, setImageHash] = React.useState(rng.getRandomId());
    const forceUpdate = React.useCallback(
        () => setImageHash(rng.getRandomId())
        , []
    );

    const [thumbLoad, setThumbLoad] = React.useState<ThumbLoadStatus>({status:'waiting'});
    
    return (
        <section>
            
            {props.data === null && 
                <EmptyPlaceholder>
                    <i>Ingen video valgt</i>
                </EmptyPlaceholder>
            }

            {props.data && props.data.status.stage === 'complete' &&
                <div style={{width:100, height:100}}>

                    <Spinner show={thumbLoad.status === 'error'} />

                    <a href={props.data.status.playerUrl}>
                        <img height="100" width="100" 
                            src={props.data.status.thumbnailUrl + '?' + imageHash} 
                            alt="Uploadet video af arbejdsopgaven"
                            style={{ 
                                display: thumbLoad.status==='loaded' ? 'block' : 'none', 
                                objectFit:'contain', 
                                backgroundColor:thumbLoad.status==='loaded' ? '#000' : '#fff' 
                            }} 
                            
                            onLoad={() =>
                            {
                                const status:ThumbLoadStatus = {status:'loaded'};
                                setThumbLoad(status);
                            }}
                            onError={() =>
                            {
                                const status:ThumbLoadStatus = thumbLoad.status === 'error' 
                                    ? {status:'error', retries:thumbLoad.retries + 1} 
                                    : {status:'error', retries:1};
                                setThumbLoad(status);
                                console.log(status);
                                if (status.retries < 60)
                                    setTimeout(() =>
                                    {
                                        forceUpdate();
                                    }, 1000);
                            }}
                        />
                    </a>
                </div>
            }
        </section>
    );
}

const EmptyPlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    height: 100px;
    width: 100px;
    border: 1px dashed #ccc;
    border-radius: 3px;
`;

const SpinnerKeyFrames = keyframes` 
    to { -webkit-transform: rotate(360deg); }
`;

const Spinner = styled.div<{show:boolean}>`
    position: absolute;
    z-index: 100000000;
    display: inline-block;
    width: 100px;
    height: 100px;
    border: 6px solid #888;
    border-radius: 50%;
    border-top-color: #ddd;
    animation: ${SpinnerKeyFrames} 1s ease-in-out infinite;
    transition: all 500ms linear;
    visibility: ${props => props.show ? 'visible' : 'hidden'};
`;